import { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, AlertCircle, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RevenueMonsterLogo } from '../components/PaymentLogos';
import NoGatewayBanner from '../components/NoGatewayBanner';

const GATEWAYS = [
  {
    key: 'billplz',
    name: 'Billplz',
    description: 'Popular Malaysian payment gateway with FPX, card & e-wallet support',
    logo: '/logos/billplz.png',
    available: true,
    signupUrl: 'https://billplz.com',
    fields: [
      { key: 'api_key', label: 'API Secret Key', type: 'password', help: 'Found in Billplz Dashboard → Settings → Keys' },
      { key: 'collection_id', label: 'Collection ID', type: 'text', help: 'Found in Billplz Dashboard → Billing → Collections' },
      { key: 'x_signature_key', label: 'X-Signature Key (Optional)', type: 'password', help: 'For webhook verification. Enable in Billplz → Settings → X-Signature' },
    ]
  },
  {
    key: 'toyyibpay',
    name: 'ToyyibPay',
    description: 'Affordable gateway for Malaysian micro-businesses',
    logo: '/logos/toyyibpay.png',
    available: true,
    signupUrl: 'https://toyyibpay.com',
    fields: [
      { key: 'secret_key', label: 'User Secret Key', type: 'password', help: 'Found in ToyyibPay Dashboard → Profile' },
      { key: 'category_code', label: 'Category Code', type: 'text', help: 'Create a category in ToyyibPay → Manage Category, then copy the code' }
    ]
  },
  {
    key: 'senangpay',
    name: 'SenangPay',
    description: 'Easy setup with broad payment coverage in Malaysia',
    logo: '/logos/senangpay.png',
    available: true,
    signupUrl: 'https://senangpay.my',
    fields: [
      { key: 'merchant_id', label: 'Merchant ID', type: 'text', help: 'Found in SenangPay Dashboard → Settings → Profile' },
      { key: 'secret_key', label: 'Secret Key', type: 'password', help: 'Found in SenangPay Dashboard → Settings → Profile' }
    ]
  },
  {
    key: 'stripe',
    name: 'Stripe',
    description: 'International gateway — accepts cards worldwide in MYR',
    logo: '/logos/stripe.png',
    available: true,
    signupUrl: 'https://stripe.com',
    fields: [
      { key: 'secret_key', label: 'Secret Key', type: 'password', help: 'Found in Stripe Dashboard → Developers → API Keys (starts with sk_)' },
      { key: 'webhook_secret', label: 'Webhook Secret (Optional)', type: 'password', help: 'Found in Stripe Dashboard → Developers → Webhooks → Signing secret (starts with whsec_)' }
    ]
  },
  {
    key: 'revenuemonster',
    name: 'Revenue Monster',
    description: 'All-in-one Malaysian payment platform',
    logo: null,
    LogoComponent: RevenueMonsterLogo,
    available: false,
    fields: [
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
      { key: 'store_id', label: 'Store ID', type: 'text' }
    ]
  },
];

export default function PaymentSettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await settingsAPI.getPayments();
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateGateway = (gatewayKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      [gatewayKey]: { ...(prev[gatewayKey] || {}), [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updatePayments(settings);
      toast.success('Payment settings saved');
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Payment Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure your payment gateways to start accepting payments</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="payment-settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save Settings
        </Button>
      </div>

      <NoGatewayBanner ctaLabel="See the guide below" />

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Each payment gateway requires you to sign up for an account directly with the provider. After signing up, enter your API keys here.
          Never share your secret keys in screenshots.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {GATEWAYS.map(gateway => {
          const gw = settings[gateway.key] || {};
          const isComingSoon = !gateway.available;
          
          return (
            <Card key={gateway.key} data-testid={`payment-gateway-${gateway.key}-card`} className={`card-hover ${isComingSoon ? 'opacity-70' : ''}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center gap-3">
                  {/* Logo */}
                  <div className="h-10 w-20 rounded-lg shrink-0 flex items-center justify-center overflow-hidden bg-white border border-border p-1">
                    {gateway.logo ? (
                      <img src={gateway.logo} alt={gateway.name} className="h-full w-full object-contain" />
                    ) : gateway.LogoComponent ? (
                      <gateway.LogoComponent className="h-8 w-full" />
                    ) : (
                      <span className="text-xs font-bold text-muted-foreground">{gateway.name.slice(0, 2)}</span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base">{gateway.name}</CardTitle>
                      {isComingSoon && <Badge variant="secondary" className="text-[10px]">Coming Soon</Badge>}
                    </div>
                    <CardDescription className="text-xs">{gateway.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!isComingSoon && (
                    <>
                      {gw.enabled ? <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>}
                      <Switch
                        checked={gw.enabled || false}
                        onCheckedChange={(v) => updateGateway(gateway.key, 'enabled', v)}
                      />
                    </>
                  )}
                </div>
              </CardHeader>
              {!isComingSoon && gw.enabled && (
                <CardContent className="space-y-4">
                  <Tabs value={gw.mode || 'test'} onValueChange={(v) => updateGateway(gateway.key, 'mode', v)}>
                    <TabsList>
                      <TabsTrigger value="test">Sandbox / Test</TabsTrigger>
                      <TabsTrigger value="live">Live</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {gateway.fields.map(field => (
                      <div key={field.key} className="space-y-1.5">
                        <Label className="text-sm">{field.label}</Label>
                        <Input
                          type={field.type}
                          value={gw[field.key] || ''}
                          onChange={e => updateGateway(gateway.key, field.key, e.target.value)}
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                        {field.help && <p className="text-[10px] text-muted-foreground">{field.help}</p>}
                      </div>
                    ))}
                  </div>
                  {gateway.signupUrl && (
                    <div className="flex items-center gap-2 pt-1">
                      <a href={gateway.signupUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> Sign up for {gateway.name} account
                      </a>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Use Sandbox/Test mode to validate your integration before accepting real payments.</p>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
